import { useCallback, useEffect, useRef, useState } from 'react'
import QRCode from 'qrcode'
import { Loader2, Upload } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from './button'
import { Card, CardContent, CardHeader, CardTitle } from './card'
import { useAuth } from '../context/AuthContext'
import { api, ApiError } from '../lib/api'
import { cn } from '../lib/utils'
import { planQuotaRows } from '../lib/plan-quota'

/**
 * Panel langganan yang dipakai bersama orisini.com dan SPA produk.
 *
 * Sejak paket disatukan (migrasi 0006 orisini-id) hanya ada SATU paket untuk
 * seluruh Orisini, jadi panel ini tidak lagi tahu atau peduli produk mana yang
 * membukanya — isinya sama persis di mana pun. Kartu "This month" menampilkan
 * pemakaian total di semua platform, karena itulah yang dibatasi paket.
 *
 * Semua request ditujukan ke ORIGIN SENDIRI (`/api/billing/*`). Di produk,
 * Worker-nya yang meneruskan ke Worker identitas lewat service binding, jadi
 * browser tidak pernah membuat permintaan lintas-origin berkredensial.
 */

interface PlanDto {
  code: string
  name: string
  description: string | null
  priceMonthly: number
  priceYearly: number
  limits: {
    maxAccounts: number
    maxPostsPerMonth: number
    maxDevices: number
  }
  extraLimits?: Record<string, number>
  features: string[]
}

interface UsageDto {
  posts: { used: number; limit: number; remaining: number }
  accounts: { used: number; max: number }
  /** Opsional: Worker identitas versi lama belum mengirimnya. */
  creations?: Record<'posters' | 'carousels' | 'videos', { used: number; max: number }>
}

interface OrderDto {
  orderNo: string
  planCode: string
  period: string
  uniqueAmount: number
  qrisPayload: string | null
  status: string
  paidVia: string | null
  expiresAt: number
  createdAt: number
}

export interface BillingPanelProps {
  /** Paket yang sedang aktif. */
  planCode?: string | null
  planName?: string | null
  /** Akhir masa berlaku, epoch ms. */
  periodEnd?: number | null
  /** Langganan masih berlaku? `false` pada paket berbayar berarti kedaluwarsa. */
  active?: boolean
  /** Downgrade ke Free sudah dijadwalkan pada periodEnd. */
  cancelAtPeriodEnd?: boolean
  /** Disisipkan di bawah judul. */
  children?: React.ReactNode
}

const rupiah = (n: number) => `Rp${n.toLocaleString('id-ID')}`

const tanggal = (ms: number) =>
  new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

const isFreePlan = (p: PlanDto) => p.priceMonthly === 0 && p.priceYearly === 0

export function BillingPanel({
  planCode,
  planName,
  periodEnd,
  active = true,
  cancelAtPeriodEnd = false,
  children,
}: BillingPanelProps) {
  const { refresh } = useAuth()
  const [plans, setPlans] = useState<PlanDto[]>([])
  const [period, setPeriod] = useState<'monthly' | 'yearly'>('monthly')
  const [order, setOrder] = useState<OrderDto | null>(null)
  const [invoices, setInvoices] = useState<OrderDto[]>([])
  const [usage, setUsage] = useState<UsageDto | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [confirmFree, setConfirmFree] = useState(false)

  // Paket berbayar yang lewat masa berlakunya. Paket Free tanpa baris
  // langganan juga datang dengan active=false, tapi itu bukan "kedaluwarsa".
  const expired = !active && Boolean(planCode) && planCode !== 'free'
  const currentIdx = plans.findIndex((p) => p.code === planCode)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const load = useCallback(async () => {
    const [plansRes, invRes, usageRes] = await Promise.all([
      api.get<{ plans: PlanDto[] }>('/billing/plans'),
      api.get<{ orders: OrderDto[] }>('/billing/invoices'),
      // Kartu pemakaian bersifat pelengkap — gagal memuatnya tidak boleh
      // menghalangi user membayar.
      api.get<UsageDto>('/billing/usage').catch(() => null),
    ])
    setPlans(plansRes.plans)
    setInvoices(invRes.orders)
    setOrder(invRes.orders.find((o) => o.status === 'pending') ?? null)
    setUsage(usageRes)
  }, [])

  useEffect(() => {
    void load().catch(() => undefined)
  }, [load])

  // Gambar QR di canvas setiap payload berubah — QR dirender DI CLIENT.
  useEffect(() => {
    if (order?.qrisPayload && canvasRef.current) {
      void QRCode.toCanvas(canvasRef.current, order.qrisPayload, {
        width: 260,
        margin: 2,
        errorCorrectionLevel: 'M',
      })
    }
  }, [order?.qrisPayload])

  // Polling status order pending tiap 4 detik — webhook bisa datang kapan saja.
  useEffect(() => {
    if (!order || order.status !== 'pending') return
    const t = setInterval(async () => {
      try {
        const res = await api.get<{ order: OrderDto }>(`/billing/orders/${order.orderNo}`)
        if (res.order.status !== 'pending') {
          setOrder(res.order)
          if (res.order.status === 'paid') {
            toast.success('Payment received — your subscription is active')
            await refresh()
            await load()
          }
        }
      } catch {
        /* jaringan — coba lagi tick berikutnya */
      }
    }, 4000)
    return () => clearInterval(t)
  }, [order, refresh, load])

  async function checkout(planToBuy: string) {
    setBusy(planToBuy)
    try {
      const res = await api.post<OrderDto & { qrisPayload: string }>('/billing/checkout', {
        planCode: planToBuy,
        period,
      })
      setOrder({
        ...res,
        status: 'pending',
        paidVia: null,
        createdAt: Date.now(),
        planCode: planToBuy,
        period,
      })
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Couldn’t create the invoice')
    } finally {
      setBusy(null)
    }
  }

  async function downgrade() {
    setBusy('free')
    try {
      const res = await api.post<{ scheduled: boolean; periodEnd: number | null }>(
        '/billing/downgrade',
      )
      toast.success(
        res.scheduled && res.periodEnd
          ? `You’ll move to Free on ${tanggal(res.periodEnd)}`
          : 'You’re on the Free plan now',
      )
      setConfirmFree(false)
      await refresh()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Couldn’t change the plan')
    } finally {
      setBusy(null)
    }
  }

  async function keepPlan() {
    setBusy(planCode ?? 'keep')
    try {
      await api.post('/billing/downgrade/cancel')
      toast.success(`You’ll stay on ${planName ?? 'your plan'}`)
      await refresh()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Couldn’t change the plan')
    } finally {
      setBusy(null)
    }
  }

  /** Label & aksi tombol tiap kartu paket, relatif terhadap paket yang sedang dipegang. */
  function planAction(p: PlanDto, idx: number) {
    const isCurrent = p.code === planCode
    if (isFreePlan(p)) {
      if (isCurrent && active) return { label: 'Current plan', disabled: true }
      if (cancelAtPeriodEnd && periodEnd)
        return { label: `Starts ${tanggal(periodEnd)}`, disabled: true }
      return {
        label: isCurrent ? 'Activate Free' : 'Downgrade to Free',
        onClick: () => (isCurrent ? void downgrade() : setConfirmFree(true)),
      }
    }
    if (isCurrent) {
      if (expired) return { label: `Renew ${p.name}`, onClick: () => void checkout(p.code) }
      if (cancelAtPeriodEnd) return { label: `Keep ${p.name}`, onClick: () => void keepPlan() }
      return { label: 'Current plan', disabled: true }
    }
    const verb = currentIdx < 0 ? 'Buy' : idx > currentIdx ? 'Upgrade to' : 'Downgrade to'
    return { label: `${verb} ${p.name}`, onClick: () => void checkout(p.code) }
  }

  async function uploadProof(file: File) {
    if (!order) return
    const form = new FormData()
    form.append('file', file)
    try {
      const res = await fetch(`/api/billing/orders/${order.orderNo}/proof`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data?.error?.message ?? 'Couldn’t upload the file')
      toast.success('Proof sent — waiting for verification')
      setOrder({ ...order, status: 'awaiting_verification' })
    } catch (err) {
      toast.error(String(err instanceof Error ? err.message : err))
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Subscription</h1>
        <p className="text-sm text-muted-foreground">
          {planName ? `${planName} plan` : 'No plan'}
          {periodEnd &&
            (expired ? (
              <span className="text-destructive"> · expired {tanggal(periodEnd)}</span>
            ) : cancelAtPeriodEnd ? (
              ` · moves to Free on ${tanggal(periodEnd)}`
            ) : (
              ` · valid until ${tanggal(periodEnd)}`
            ))}
        </p>
      </header>

      {children}

      {expired && (
        <Card className="border-destructive/50">
          <CardContent className="p-4 text-sm">
            Your {planName} plan expired on {periodEnd ? tanggal(periodEnd) : 'its end date'}.
            Scheduling is paused until you renew — your posts and queue are kept. Renew below, or
            move to Free.
          </CardContent>
        </Card>
      )}

      {usage && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">This month</CardTitle>
            <p className="text-xs text-muted-foreground">
              One plan covers every platform. Posts and accounts count Pinterest, Instagram, and
              Threads together; posters, carousels, and video minutes each have their own limit. Videos over 10 minutes render on your computer and use none.
            </p>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <UsageMeter label="Posts" used={usage.posts.used} max={usage.posts.limit} />
            <UsageMeter
              label="Connected accounts"
              used={usage.accounts.used}
              max={usage.accounts.max}
            />
            {usage.creations && (
              <>
                <UsageMeter
                  label="Posters"
                  used={usage.creations.posters.used}
                  max={usage.creations.posters.max}
                />
                <UsageMeter
                  label="Carousels"
                  used={usage.creations.carousels.used}
                  max={usage.creations.carousels.max}
                />
                <UsageMeter
                  label="Video minutes"
                  used={usage.creations.videos.used}
                  max={usage.creations.videos.max}
                />
              </>
            )}
          </CardContent>
        </Card>
      )}

      {order && order.status === 'pending' && (
        <Card className="border-primary/40">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Complete your payment</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-start gap-6">
            <canvas ref={canvasRef} className="rounded-lg bg-white p-2" />
            <div className="min-w-52 flex-1 space-y-2 text-sm">
              <p>
                Scan the QRIS code above with any e-wallet and pay{' '}
                <strong className="text-lg text-foreground">{rupiah(order.uniqueAmount)}</strong>
              </p>
              <p className="text-xs text-amber-500">
                Pay EXACTLY that amount — the last three digits are a unique code used to match
                your payment automatically.
              </p>
              <p className="text-xs text-muted-foreground">
                Order {order.orderNo} · expires{' '}
                {new Date(order.expiresAt).toLocaleTimeString('en-GB', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })}
                . This page checks the status for you — leave it open after paying.
              </p>
              <div className="flex gap-2 pt-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) void uploadProof(f)
                  }}
                />
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                  <Upload />
                  Already paid but not detected? Send proof
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    void api.post(`/billing/orders/${order.orderNo}/cancel`).then(() => {
                      setOrder(null)
                      void load()
                    })
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {order && order.status === 'awaiting_verification' && (
        <Card className="border-amber-500/40">
          <CardContent className="p-4 text-sm">
            Order {order.orderNo}: an admin is verifying your payment proof. The subscription
            activates as soon as it is approved.
          </CardContent>
        </Card>
      )}

      {plans.length > 0 && (
        <div className="flex justify-center gap-1 rounded-md bg-muted p-1 w-fit mx-auto">
          {(
            [
              ['monthly', 'Monthly'],
              ['yearly', 'Yearly (save ~2 months)'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              onClick={() => setPeriod(id)}
              className={cn(
                'rounded px-4 py-1.5 text-sm',
                period === id ? 'bg-background font-medium shadow-sm' : 'text-muted-foreground',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        {plans.map((p, idx) => {
          const price = period === 'yearly' ? p.priceYearly : p.priceMonthly
          const isCurrent = p.code === planCode
          const action = planAction(p, idx)
          return (
            <Card
              key={p.code}
              className={cn(
                isCurrent && (expired ? 'border-destructive/50' : 'border-primary/50'),
              )}
            >
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  {p.name}
                  {isCurrent && (
                    <span
                      className={cn(
                        'rounded px-1.5 py-0.5 text-xs font-normal',
                        expired
                          ? 'bg-destructive/15 text-destructive'
                          : 'bg-primary/15 text-primary',
                      )}
                    >
                      {expired ? 'Expired' : 'Current'}
                    </span>
                  )}
                </CardTitle>
                <p className="text-2xl font-semibold">
                  {price === 0 ? 'Free' : rupiah(price)}
                  {price > 0 && (
                    <span className="text-sm font-normal text-muted-foreground">
                      /{period === 'yearly' ? 'year' : 'month'}
                    </span>
                  )}
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Satu baris per jenis kuota, diturunkan dari angka paketnya. */}
                <dl className="divide-y text-sm">
                  {planQuotaRows(p).map((r) => (
                    <div key={r.key} className="flex items-baseline justify-between gap-3 py-1.5">
                      <dt className="text-muted-foreground">{r.label}</dt>
                      <dd className="font-medium tabular-nums">{r.value}</dd>
                    </div>
                  ))}
                </dl>
                {isFreePlan(p) && confirmFree ? (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">
                      {!expired && planCode !== 'free' && periodEnd
                        ? `You keep ${planName} until ${tanggal(periodEnd)}, then move to Free. Nothing is charged.`
                        : 'You move to Free now. Free plan limits apply from today.'}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        className="flex-1"
                        disabled={busy !== null}
                        onClick={() => void downgrade()}
                      >
                        {busy === 'free' ? <Loader2 className="animate-spin" /> : null}
                        Confirm
                      </Button>
                      <Button
                        className="flex-1"
                        variant="ghost"
                        disabled={busy !== null}
                        onClick={() => setConfirmFree(false)}
                      >
                        Back
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    className="w-full"
                    variant={action.disabled ? 'secondary' : isFreePlan(p) ? 'outline' : 'default'}
                    disabled={action.disabled || busy !== null}
                    onClick={action.onClick}
                  >
                    {busy === p.code ? <Loader2 className="animate-spin" /> : null}
                    {action.label}
                  </Button>
                )}
              </CardContent>
            </Card>
          )
        })}
      </div>

      {invoices.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Billing history</CardTitle>
          </CardHeader>
          <CardContent className="divide-y p-0">
            {invoices.map((o) => (
              <div key={o.orderNo} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                <span className="w-44 shrink-0 font-mono text-xs">{o.orderNo}</span>
                <span className="w-20 shrink-0">{o.planCode}</span>
                <span className="w-28 shrink-0 tabular-nums">{rupiah(o.uniqueAmount)}</span>
                <span
                  className={cn(
                    'shrink-0 rounded px-1.5 py-0.5 text-xs',
                    o.status === 'paid'
                      ? 'bg-emerald-500/15 text-emerald-400'
                      : o.status === 'pending' || o.status === 'awaiting_verification'
                        ? 'bg-amber-500/15 text-amber-500'
                        : 'bg-muted text-muted-foreground',
                  )}
                >
                  {o.status}
                </span>
                <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                  {new Date(o.createdAt).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function UsageMeter({ label, used, max }: { label: string; used: number; max: number }) {
  const pct = max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 100
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="tabular-nums">
          {used.toLocaleString('en-US')} / {max.toLocaleString('en-US')}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
        <div
          className={cn('h-full rounded-full', pct >= 100 ? 'bg-destructive' : 'bg-primary')}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

export default BillingPanel
